import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { listLoadJobs } from '@/lib/api';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import {
  completionFromStatus,
  LoadJobsContext,
  type ActiveLoadJob,
  type LoadCompletion,
} from '@/lib/loadJobs';

const POLL_MS = 3000;
// Watched jobs live in localStorage (not sessionStorage) so they survive a
// closed tab, not just a reload — the whole point is that the result is not
// tied to the browser session that started the load.
const WATCH_KEY = 'tres.loadJobs.watch';

function loadWatch(): Map<string, ActiveLoadJob> {
  const m = new Map<string, ActiveLoadJob>();
  try {
    const raw = localStorage.getItem(WATCH_KEY);
    if (!raw) return m;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return m;
    for (const j of parsed) {
      if (
        typeof j === 'object' &&
        j !== null &&
        typeof (j as ActiveLoadJob).id === 'string' &&
        typeof (j as ActiveLoadJob).filename === 'string' &&
        typeof (j as ActiveLoadJob).graph === 'string' &&
        typeof (j as ActiveLoadJob).startedAt === 'number'
      ) {
        m.set((j as ActiveLoadJob).id, j as ActiveLoadJob);
      }
    }
  } catch {
    // Storage unavailable or corrupt — start empty; the tracker still works for
    // the life of the page.
  }
  return m;
}

function saveWatch(m: Map<string, ActiveLoadJob>): void {
  try {
    if (m.size === 0) localStorage.removeItem(WATCH_KEY);
    else localStorage.setItem(WATCH_KEY, JSON.stringify([...m.values()]));
  } catch {
    // Storage unavailable — the in-memory map is still authoritative for this
    // page; it just can't survive a reload.
  }
}

/**
 * Tracks the background load jobs THIS browser started, decoupled from a live
 * token. A load surface hands a job id to `addLoadJob`; the provider persists it
 * (localStorage — survives a reload or a closed tab) and reconciles its watched
 * jobs against the server's "my jobs" list on mount, on tab-focus, and on an
 * interval. A load that finishes while the id-token is expired — or while the
 * tab is away — is reported the next time the session is valid: the result lives
 * in the server job store for a day, so an expired token is "check again when
 * signed in," never "lost track." A finished job is reported once, then dropped
 * from the watch set (and from storage), so it is not re-announced on reload.
 */
export function LoadJobsProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const [active, setActive] = useState<ActiveLoadJob[]>(() => [...loadWatch().values()]);
  const [completions, setCompletions] = useState<LoadCompletion[]>([]);

  const watch = useRef<Map<string, ActiveLoadJob>>(loadWatch());
  // Read fresh each tick: if the session ever renews the id-token, the next poll
  // picks it up without re-mounting.
  const tokenRef = useRef(auth.user?.id_token);
  tokenRef.current = auth.user?.id_token;
  // A slow list read can outlast the interval; this keeps ticks from overlapping.
  const polling = useRef(false);

  const addLoadJob = useCallback((id: string, meta: Omit<ActiveLoadJob, 'id'>) => {
    if (watch.current.has(id)) return;
    watch.current.set(id, { id, ...meta });
    saveWatch(watch.current);
    setActive([...watch.current.values()]);
    // The load gate polls slowly while nothing runs; this load is now running.
    void queryClient.invalidateQueries({ queryKey: ['load-gate'] });
  }, [queryClient]);

  const dismissCompletion = useCallback((id: string) => {
    setCompletions((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const reconcile = useCallback(async () => {
    if (polling.current) return;
    const token = tokenRef.current;
    if (!token || watch.current.size === 0) return;
    polling.current = true;
    try {
      const res = await listLoadJobs(token);
      // Expired token or a transient failure: leave the watched jobs alone and
      // try again next tick / on the next valid session. NEVER give a running
      // load up for lost — the server holds the result for a day.
      if (!res.ok) return;
      const byId = new Map(res.data.jobs.map((j) => [j.job_id, j]));
      const finished: LoadCompletion[] = [];
      let changed = false;
      for (const [id, meta] of [...watch.current]) {
        const job = byId.get(id);
        if (!job) {
          // Aged out of the server's day-long window — stop watching it.
          watch.current.delete(id);
          changed = true;
          continue;
        }
        if (job.state === 'running') continue;
        const completion = completionFromStatus(job, meta);
        if (completion) finished.push(completion);
        watch.current.delete(id);
        changed = true;
      }
      if (changed) {
        saveWatch(watch.current);
        setActive([...watch.current.values()]);
      }
      if (finished.length > 0) {
        setCompletions((prev) => [...prev, ...finished]);
        // A successful load changed the store — refresh every mounted read.
        if (finished.some((c) => c.ok)) invalidateAfterDataWrite(queryClient);
      }
    } finally {
      polling.current = false;
    }
  }, [queryClient]);

  useEffect(() => {
    void reconcile();
    const interval = setInterval(() => void reconcile(), POLL_MS);
    const onFocus = () => void reconcile();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [reconcile]);

  return (
    <LoadJobsContext.Provider value={{ active, completions, addLoadJob, dismissCompletion }}>
      {children}
    </LoadJobsContext.Provider>
  );
}
